import { useEffect, useState, useCallback } from "react";
import { Box, Grid2, Typography } from "@mui/material";
import { ThemeProperties } from "../../Theme/ThemeProperties";
import { decryptTheParams } from "../../Helper/QueryParams/EncryptDecrypt";
import ScreenContainer from "../../Components/ScreenContainer";
import BatteryIntelligenceTabs from "../../PagesContent/BatteryIntelligence/BatteryIntelligenceTabs";
import Events from "../../Analytics/Events";
import CODE from "../../Static/Constants/StatusCodes";
import SOHAndRULTab from "../../PagesContent/BatteryIntelligence/SOHAndRUL/SOHAndRULTab";
import { getConnectedBatteries, getPagesMetaData, getOrgInfo } from "../../Api/Api";
import OnboardingLocationFilter from "../../PagesContent/Overview/components/OnboardingLocationFilter";
import GlobalFilter from "../../PagesContent/Overview/components/GlobalFilter";
import FilterBar from "../../Components/FilterBar";
import useGlobalFilterData from "../../Hooks/useGlobalFilterData";
import AppCache from "../../Helper/Common/AppCache";
import ConfigurationFilter from "../../PagesContent/BatteryIntelligence/Components/ConfigurationFilter";
import OperationalStatusMultiSelect from "../../PagesContent/Common/OperationalStatusMultiSelect";

const tabs = [
  {
    slug: "soh-and-rul",
    name: "SOH & RUL",
    color: ThemeProperties.purple,
  },
];

const BatteryIntelligence = () => {
  const queryParams = decryptTheParams();
  const defaultTabSlug = queryParams.tab
    ? JSON.parse(queryParams.tab)
    : tabs[0]?.slug;

  const [pagesContent, setPagesContent] = useState({ mounted: false });
  const [selectedTab, setSelectedTab] = useState(defaultTabSlug);
  const [vehiclesConnected, setVehiclesConnected] = useState(0);
  const [isLoadingVehiclesCount, setIsLoadingVehiclesCount] = useState(false);
  const [showSOHDetails, setShowSOHDetails] = useState(false);
  const [showRULDetails, setShowRULDetails] = useState(false);

  // Shared with the other global-filter pages: picks the RBAC or the legacy
  // fleet/location APIs via useCanUseGlobalAdvancedFilter (RBAC v2 only).
  const {
    allFleets,
    selectedFleets,
    allOnboardingLocationsHierarchy,
    globalFilters,
    setGlobalFilters,
    fetchFleets,
    fetchOnboardingLocations,
    handleFleetAndLocationChange,
    canUseGlobalAdvancedFilter,
    fleetsLoaded,
    filtersLoading,
  } = useGlobalFilterData();

  const globalFilterConfig =
    AppCache.getItem("globalFilter")?.find((f) => f.type === "unifiedGlobalFilter") || null;

  const fetchVehiclesConnected = useCallback(async () => {
    setIsLoadingVehiclesCount(true);
    try {
      const response = await getConnectedBatteries(globalFilters, 'batteryIntelligence');
      if (response?.responseStatus?.code === CODE.SUCCESS) {
        setVehiclesConnected(response?.response?.data?.[0]?.count || 0);
      }
    } catch (error) {
      console.error("Error fetching vehicles connected:", error);
      setVehiclesConnected(0);
    } finally {
      setIsLoadingVehiclesCount(false);
    }
  }, [globalFilters]);

  async function getPageMeta() {
    const pageName = "batteryIntelligence";
    const [res, orgRes] = await Promise.all([getPagesMetaData(pageName), getOrgInfo()]);
    Events("batteryIntelligence page opened");

    const pageMetaInfo = res?.responseStatus?.code === CODE.SUCCESS
      ? (res.response?.response?.metaInfo || {})
      : {};

    const orgMetaInfo = orgRes?.responseStatus?.code === CODE.SUCCESS
      ? (orgRes.response?.metaInfo || {})
      : {};

    const mergedMetaInfo = {
      ...pageMetaInfo,
      ...orgMetaInfo,
      BatteryIntelligence: {
        ...(pageMetaInfo.BatteryIntelligence || {}),
        ...(orgMetaInfo.BatteryIntelligence || {}),
      },
    };

    // orgBatteryIntelligence keeps the org-level list un-merged: the backend builds its
    // RUL bucket CASE from orgMeta.metaInfo.BatteryIntelligence only, never from pageMeta
    // (see getRULServerBucketUnits).
    setPagesContent((prev) => ({
      ...prev,
      metaInfo: mergedMetaInfo,
      orgBatteryIntelligence: orgMetaInfo.BatteryIntelligence || {},
      mounted: true,
    }));
  }

  useEffect(() => {
    getPageMeta();
  }, []);

  // fetchFleets is a plain function from useGlobalFilterData, so its reference
  // changes each render. Mount-only is the intent here.
  useEffect(() => {
    fetchFleets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Same stability caveat for fetchOnboardingLocations; the deps that actually
  // control when this re-runs are the data values listed.
  useEffect(() => {
    if (!fleetsLoaded || selectedFleets.length === 0) return;
    fetchOnboardingLocations(selectedFleets);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fleetsLoaded, allFleets, selectedFleets]);

  useEffect(() => {
    if ((globalFilters?.fleets || []).length > 0) {
      fetchVehiclesConnected();
    }
  }, [globalFilters, fetchVehiclesConnected]);

  const pageGlobalFiltersConfig = pagesContent?.metaInfo?.BatteryIntelligence?.SOHAndRUL?.globalFilters || {};

  return (
    <ScreenContainer pagesContent={pagesContent}>
      {!showSOHDetails && !showRULDetails && <Box
        sx={{
          position: "sticky",
          top: "50px",
          zIndex: 1000,
          backgroundColor: "#FAFAFA",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pt: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flex: 1,
              justifyContent: "space-between",
              pr: 3,
            }}
          >
            <Box sx={{ px: 3, display: "flex" }}>
              <Typography variant="fw700s20lh24c2E2E2E">
                Battery Intelligence
              </Typography>
            </Box>
            <FilterBar
              testId="battery-intelligence-filters"
              filters={[
                pageGlobalFiltersConfig.configuration === true && {
                  key: "configuration",
                  node: (
                    <ConfigurationFilter
                      selectedValues={globalFilters?.configuration}
                      onChange={(selected) =>
                        setGlobalFilters((prev) => ({ ...prev, configuration: selected }))
                      }
                    />
                  ),
                },
                pageGlobalFiltersConfig.operationalStatus === true && {
                  key: "operationalStatus",
                  node: (
                    <OperationalStatusMultiSelect
                      selectedValues={globalFilters?.operationalStatus}
                      onSelectionChange={(selected) =>
                        setGlobalFilters((prev) => ({ ...prev, operationalStatus: selected }))
                      }
                      onOptionsLoaded={(allCodes, defaultSelection) =>
                        setGlobalFilters((prev) => ({
                          ...prev,
                          operationalStatus: prev.operationalStatus ?? defaultSelection,
                        }))
                      }
                      // [] sends no header (same as before the options load) and marks the
                      // filter settled, so cards waiting for it (SOH bucket migration) load.
                      onOptionsError={() =>
                        setGlobalFilters((prev) => ({ ...prev, operationalStatus: prev.operationalStatus ?? [] }))
                      }
                    />
                  ),
                },
                pageGlobalFiltersConfig.location === true && {
                  key: "customerLocation",
                  primary: true,
                  node: canUseGlobalAdvancedFilter && globalFilterConfig ? (
                    <GlobalFilter
                      isLoading={filtersLoading}
                      allFleets={allFleets}
                      allOnboardingLocationsHierarchy={allOnboardingLocationsHierarchy}
                      selectedFleetIds={globalFilters?.fleets || []}
                      selectedLocations={globalFilters?.hierarchicalLocation}
                      onChange={handleFleetAndLocationChange}
                    />
                  ) : (
                    <OnboardingLocationFilter
                      locations={allOnboardingLocationsHierarchy}
                      selectedLocations={globalFilters?.hierarchicalLocation}
                      onChange={(selected) => {
                        setGlobalFilters((prev) => ({
                          ...prev,
                          hierarchicalLocation: selected,
                        }));
                      }}
                    />
                  ),
                },
              ]}
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flex: 1,
            justifyContent: "space-between",
            borderBottom: "1px solid #E4E3E3",
            mt: 0,
            pr: 3,
          }}
        >
          <BatteryIntelligenceTabs
            tabs={tabs}
            selectedTab={selectedTab}
            changeTheTab={(tabId) => {
              setSelectedTab(tabId);
            }}
          />
        </Box>
      </Box>}

      {/* Page Content */}
      <Box
        sx={{
          px: 3,
          py: 2,
          mb: 2,
          height: "auto",
          overflow: "visible",
        }}
      >
        <Grid2
          container
          gap={2}
          flexDirection={"column"}
          sx={{ height: "auto", minWidth: 0 }}
        >
          {selectedTab === "soh-and-rul" && (globalFilters?.fleets || []).length > 0 && (
            <SOHAndRULTab
              vehiclesConnected={vehiclesConnected}
              isLoadingVehiclesCount={isLoadingVehiclesCount}
              globalFilters={globalFilters}
              allOnboardingLocationsHierarchy={allOnboardingLocationsHierarchy}
              allFleets={allFleets}
              showSOHDetails={showSOHDetails}
              onShowSOHDetails={setShowSOHDetails}
              showRULDetails={showRULDetails}
              onShowRULDetails={setShowRULDetails}
              waitForOperationalStatus={pageGlobalFiltersConfig.operationalStatus === true}
            />
          )}
        </Grid2>
      </Box>
    </ScreenContainer>
  );
};

export default BatteryIntelligence;
